/**
 * Inactive Customers Modal
 *
 * Displays detailed information about inactive customers:
 * - Customer list
 * - Last purchase date
 * - Last 3 invoices with items and categories
 */

import { useEffect, useState } from "react";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ArrowRight, Package } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { formatThaiDate } from "@/shared/lib/rfm-translations";

interface InactiveCustomer {
  customerId: string;
  customerName: string;
  lastPurchaseDate: string | null;
  invoices: Array<{
    invoiceCode: string;
    paidDate: string | null;
    amount: number;
    mainCategory: string;
    itemCount: number;
    items: Array<{
      sku: string;
      description: string;
      quantity: number;
      unitPrice: number;
    }>;
  }>;
}

interface InactiveCustomersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  months: 1 | 2 | 3 | 6;
  period: string;
  previewAs?: string; // Admin preview mode: view as specific salesperson
}

// Helper to calculate the historical period when customers were last active
const getHistoricalPeriod = (currentPeriod: string, months: number): string => {
  const [year, month] = currentPeriod.split('-').map(Number);

  let historicalMonth = month - months;
  let historicalYear = year;

  while (historicalMonth <= 0) {
    historicalMonth += 12;
    historicalYear -= 1;
  }

  return `${historicalYear}-${String(historicalMonth).padStart(2, '0')}`;
};

export function InactiveCustomersModal({
  open,
  onOpenChange,
  months,
  period,
  previewAs
}: InactiveCustomersModalProps) {
  const api = useAuthenticatedApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<InactiveCustomer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);

  // Calculate the historical period when these customers were last active
  const historicalPeriod = getHistoricalPeriod(period, months);

  useEffect(() => {
    if (open) {
      fetchInactiveCustomers();
    }
  }, [open, months, period, previewAs]);

  const fetchInactiveCustomers = async () => {
    setLoading(true);
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("period", period);

      if (previewAs) {
        searchParams.set("previewAs", previewAs);
      }

      const response = await api.request(
        `/api/sales/customers/inactive/${months}?${searchParams}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch inactive customers");
      }

      const result = await response.json() as {
        success: boolean;
        data: {
          customers: InactiveCustomer[];
          totalInactiveCustomers: number;
        };
      };
      setCustomers(result.data.customers || []);
      setTotalCount(result.data.totalInactiveCustomers || 0);
    } catch (error) {
      console.error("Error fetching inactive customers:", error);
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getMonthsLabel = () => {
    switch (months) {
      case 1: return "1 Month";
      case 2: return "2 Months";
      case 3: return "3 Months";
      case 6: return "6 Months";
      default: return `${months} Months`;
    }
  };

  const toggleCustomerExpansion = (customerId: string) => {
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Customers Inactive for {getMonthsLabel()}
          </DialogTitle>
          <DialogDescription>
            {totalCount} customers haven't made purchases in the last {getMonthsLabel().toLowerCase()}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No inactive customers found for this period.
          </div>
        ) : (
          <div className="space-y-4">
            {customers.map((customer) => (
              <Card key={customer.customerId} className="border-orange-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{customer.customerName}</CardTitle>
                      <CardDescription className="mt-1">
                        Customer ID: {customer.customerId} • Last Purchase: {formatThaiDate(customer.lastPurchaseDate)}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleCustomerExpansion(customer.customerId)}
                      >
                        <Package className="h-4 w-4 mr-1" />
                        {expandedCustomer === customer.customerId ? "Hide" : "View"} Invoices ({customer.invoices.length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigate(`/analytics/customers/${customer.customerId}?period=${historicalPeriod}`);
                          onOpenChange(false);
                        }}
                      >
                        Details
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {expandedCustomer === customer.customerId && customer.invoices.length > 0 && (
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-muted-foreground mb-3">
                        Last {customer.invoices.length} Invoice{customer.invoices.length > 1 ? 's' : ''}:
                      </p>
                      {customer.invoices.map((invoice) => (
                        <div key={invoice.invoiceCode} className="border rounded-lg p-4 space-y-3 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium font-mono text-sm">{invoice.invoiceCode}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatThaiDate(invoice.paidDate)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-lg">
                                ฿{invoice.amount.toLocaleString()}
                              </p>
                              <Badge variant="outline" className="mt-1">
                                {invoice.mainCategory}
                              </Badge>
                            </div>
                          </div>

                          {invoice.items.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                Items ({invoice.itemCount} total):
                              </p>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-xs">SKU</TableHead>
                                    <TableHead className="text-xs">Description</TableHead>
                                    <TableHead className="text-xs text-right">Qty</TableHead>
                                    <TableHead className="text-xs text-right">Unit Price</TableHead>
                                    <TableHead className="text-xs text-right">Total</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {invoice.items.map((item, itemIdx) => (
                                    <TableRow key={itemIdx}>
                                      <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                                      <TableCell className="text-xs">{item.description}</TableCell>
                                      <TableCell className="text-xs text-right">{item.quantity}</TableCell>
                                      <TableCell className="text-xs text-right">
                                        ฿{item.unitPrice.toLocaleString()}
                                      </TableCell>
                                      <TableCell className="text-xs text-right font-medium">
                                        ฿{(item.quantity * item.unitPrice).toLocaleString()}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              {invoice.itemCount > invoice.items.length && (
                                <p className="text-xs text-muted-foreground mt-2 text-center">
                                  ... and {invoice.itemCount - invoice.items.length} more item(s)
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
